import { useAppDispatch, useAppSelector } from 'app/hooks';
import { setTaskFilter } from 'app/slices/dashboardSlice';
import { useWorkflowTasks, useCriticalTask } from 'hooks/useDashboardData';
import styles from './StatusesRail.module.scss';

/**
 * Left Tasks / workflow filter rail (§3.C). READ-ONLY filter rail.
 *
 * KEY PARITY FIX: rendered as nav LINKS (not radio buttons) while preserving the
 * green active highlight (#427639 bg, #3a5435 hover) and the white `\e021` arrow
 * marker (shown only on the active link AND only while the rail is expanded —
 * handled by the scoped CSS module).
 *
 * "All" (icon-sum-sign, value '') is first and selected by default. One link per
 * workflow task (icon-{id} + name) from GET .../workflow/tasks/. Clicking a task
 * sets dashboardSlice.taskFilter ('' = All) which drives BOTH the main and pinned
 * list filters downstream (in a later sub-phase). The link whose id === the
 * critical task's task_id gets an extra `criticalTask` class (green tint #52824f).
 */
const StatusesRail = () => {
  const dispatch = useAppDispatch();
  const taskFilter = useAppSelector((s) => s.dashboard.taskFilter);
  const { data: tasks = [] } = useWorkflowTasks();
  const { data: critical } = useCriticalTask();
  const criticalId = critical?.task_id;

  const onSelect = (value: string) => () => dispatch(setTaskFilter(value));

  const linkClass = (value: string, isCritical: boolean) =>
    [
      styles.statusLink,
      'status',
      'ka-has-tooltip',
      taskFilter === value ? `${styles.active} active` : '',
      isCritical ? `${styles.criticalTask} critical_task` : '',
    ]
      .filter(Boolean)
      .join(' ');

  return (
    <nav id="statuses_nav">
      {/* All — default selection (value '') */}
      <a
        id="status_0"
        className={linkClass('', false)}
        // eslint-disable-next-line jsx-a11y/anchor-is-valid
        role="button"
        tabIndex={0}
        data-value=""
        tooltip="All"
        onClick={onSelect('')}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') onSelect('')();
        }}
      >
        <i className="icon-sum-sign" />
        <span>All</span>
      </a>

      {tasks.map((task) => {
        const value = String(task.id);
        const isCritical = criticalId != null && task.id === criticalId;
        return (
          <a
            key={task.id}
            id={`status_${task.id}`}
            className={linkClass(value, isCritical)}
            // eslint-disable-next-line jsx-a11y/anchor-is-valid
            role="button"
            tabIndex={0}
            data-value={value}
            tooltip={task.name}
            onClick={onSelect(value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') onSelect(value)();
            }}
          >
            <i className={`icon-${task.id}`} />
            <span>{task.name}</span>
          </a>
        );
      })}
    </nav>
  );
};

export default StatusesRail;
